import type { ReactNode } from "react";
import { CircleAlert, CircleCheck, Info } from "lucide-react";

/**
 * The centred card every sign-in screen sits in: no portal navigation, because the person has no party to navigate yet.
 * Colours come from the shell's CSS variables, so each portal draws it in its own look.
 */
export function SignInFrame({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
}: {
  /** The small line above the heading: the portal's brand, or the party the screen is for. */
  eyebrow: string;
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-muted/40">
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-4">
          <div className="space-y-1 text-center">
            <p className="text-sm font-semibold tracking-tight">{eyebrow}</p>
            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
            {subtitle ? (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
          {children}
          {footer ? (
            <div className="text-center text-xs text-muted-foreground">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const NOTICE_TONES = {
  info: { className: "bg-blue-50 text-blue-900", Icon: Info },
  ok: { className: "bg-green-50 text-green-900", Icon: CircleCheck },
  warn: { className: "bg-amber-50 text-amber-900", Icon: CircleAlert },
  problem: { className: "bg-red-50 text-red-900", Icon: CircleAlert },
} as const;

/** A line of text above the ways in a sign-in card, in the tone the situation needs: a problem to fix (a wrong code),
 *  a warning (the wrong account came back), information, or something done. */
export function Notice({
  tone = "info",
  children,
}: {
  tone?: keyof typeof NOTICE_TONES;
  children: ReactNode;
}) {
  const { className, Icon } = NOTICE_TONES[tone];
  return (
    <div
      role={tone === "problem" || tone === "warn" ? "alert" : undefined}
      className={`flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-sm ${className}`}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}
